/**
 * Product management API (via pro-api BFF → product-service)
 */

import { authenticatedProApiFetch } from './pro-api.client';
import type { EffectiveProductAccess } from './product-access';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ProductLine =
  | 'sanctuary'
  | 'peak'
  | 'sanctuary_1'
  | 'sanctuary_2'
  | 'sanctuary_3'
  | 'vault'
  | 'cabinet'
  | 'container'
  | 'summit'
  | 'safari';

export const BLUETOOTH_ONLY_LINES: readonly ProductLine[] = ['summit', 'safari'];

export function isBluetoothOnlyLine(line: ProductLine | string | null | undefined): boolean {
  return !!line && (BLUETOOTH_ONLY_LINES as readonly string[]).includes(line);
}

export const PRODUCT_LINE_LABELS: Record<ProductLine, string> = {
  sanctuary: 'Sanctuary',
  peak: 'Peak',
  sanctuary_1: 'Sanctuary 1',
  sanctuary_2: 'Sanctuary 2',
  sanctuary_3: 'Sanctuary 3',
  vault: 'Vault',
  cabinet: 'Cabinet',
  container: 'Container',
  summit: 'Summit',
  safari: 'Safari',
};

export type ProductStatus = 'active' | 'inactive' | 'decommissioned';
export type ProductSource = 'lion_energy' | 'solarman' | 'smart';
export type ProductOrgRole = 'owner' | 'installer' | 'viewer';

export interface ProductModel {
  id: string;
  productLine: ProductLine;
  name: string;
  modelNumber?: string | null;
  description?: string | null;
  hasWifi: boolean;
  hasBluetooth: boolean;
  isActive: boolean;
  smartCategoryProductId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProductOrganization {
  id: string;
  productId: string;
  organizationId: string;
  role: ProductOrgRole;
  createdAt: string;
}

export interface ProductSmartLinkSummary {
  syncStatus: 'in_sync' | 'out_of_sync' | 'unknown';
  linkEnabled: boolean;
  environment: 'primary' | 'alt';
  lastSyncedAt: string | null;
  smartProductId: string;
}


export interface Product {
  id: string;
  isMocked: boolean;
  productModelId?: string | null;
  productModel?: ProductModel | null;
  organizations: ProductOrganization[];
  assetTag?: string | null;
  serial?: string | null;
  macAddress?: string | null;
  /** IoT Core thing name = Smart API product UUID. Bridge to AWS infrastructure. */
  iotThingName?: string | null;
  /** Per-product DataCatalog override. Overrides the model-level catalog when set. */
  dataCatalogId?: string | null;
  name: string;
  source: ProductSource;
  smartLink?: ProductSmartLinkSummary | null;
  status: ProductStatus;
  isSimulated: boolean;
  /** True while a simulation session is actively running. */
  isSimulating: boolean;
  /** ISO datetime — when the current simulation session auto-expires. */
  simulationExpAt?: string | null;
  /** ISO datetime — when the current simulation session started. */
  simulationStartedAt?: string | null;
  pvCapacityW?: string | null;
  latitude?: string | null;
  longitude?: string | null;
  address?: string | null;
  addressSecondary?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  country?: string | null;
  timezone?: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
  createdById?: string | null;
  deletedById?: string | null;
  /** URL of the first (main) product image, if the backend includes it in the list response. */
  mainImageUrl?: string | null;
  /** Resolved IoT domain ('sanctuary' | 'powersave') derived from productLine. */
  domain?: string | null;
  /** ISO timestamp of the most recent telemetry payload (from heartbeat cache). */
  lastTelemetryAt?: string | null;
  /** ISO timestamp of the most recent shadow update received from the device (from heartbeat cache). */
  lastShadowAt?: string | null;
  /** Max of lastTelemetryAt and lastShadowAt — use for health status derivation. */
  lastSignalAt?: string | null;
}

export interface ProductModelSummary {
  id: string;
  name: string;
  productLine: ProductLine;
  modelNumber?: string | null;
}

export interface ProductListItem {
  id: string;
  name: string;
  status: ProductStatus;
  isSimulated: boolean;
  isSimulating: boolean;
  simulationExpAt?: string | null;
  simulationStartedAt?: string | null;
  isMocked: boolean;
  source: ProductSource;
  smartLink?: ProductSmartLinkSummary | null;
  productModelId?: string | null;
  productModel?: ProductModelSummary | null;
  organizations: Pick<ProductOrganization, 'organizationId' | 'role'>[];
  assetTag?: string | null;
  serial?: string | null;
  macAddress?: string | null;
  city?: string | null;
  state?: string | null;
  latitude?: string | null;
  longitude?: string | null;
  createdAt: string;
  lastTelemetryAt?: string | null;
  lastShadowAt?: string | null;
  lastSignalAt?: string | null;
  /** Resolved IoT domain ('sanctuary' | 'powersave') derived from productLine. */
  domain?: string | null;
  access?: EffectiveProductAccess | null;
}

export interface ProductsListResponse {
  data: ProductListItem[];
  total: number;
  page: number;
  limit: number;
}

export interface ProductModelsListResponse {
  data: ProductModel[];
  total: number;
}

// ─── Product Models API ───────────────────────────────────────────────────────

export async function fetchProductModels(
  accessToken: string,
  includeInactive = false,
): Promise<ProductModel[]> {
  const endpoint = includeInactive
    ? '/product-models?include_inactive=true'
    : '/product-models';
  const res = await authenticatedProApiFetch<ProductModelsListResponse>(endpoint, accessToken);
  return res.data;
}

export async function createProductModel(
  payload: Omit<ProductModel, 'id' | 'createdAt' | 'updatedAt'>,
  accessToken: string,
): Promise<ProductModel> {
  return authenticatedProApiFetch<ProductModel>('/product-models', accessToken, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateProductModel(
  accessToken: string,
  modelId: string,
  payload: Partial<Omit<ProductModel, 'id' | 'productLine' | 'createdAt' | 'updatedAt'>>,
): Promise<ProductModel> {
  return authenticatedProApiFetch<ProductModel>(`/product-models/${modelId}`, accessToken, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

// ─── Products API ─────────────────────────────────────────────────────────────

export async function fetchProductsList(
  params: {
    page?: number
    limit?: number
    organizationId?: string
    organizationIds?: string[]
    status?: ProductStatus | ProductStatus[]
    search?: string
    productLines?: string[]
    productModelIds?: string[]
  },
  accessToken: string,
): Promise<ProductsListResponse> {
  const query = new URLSearchParams();
  if (params.page) query.set('page', String(params.page));
  if (params.limit) query.set('limit', String(params.limit));
  if (params.organizationId) query.set('organization_id', params.organizationId);
  if (params.organizationIds?.length) query.set('organization_ids', params.organizationIds.join(','));
  if (params.status) {
    const statusCsv = Array.isArray(params.status) ? params.status.join(',') : params.status;
    if (statusCsv) query.set('status', statusCsv);
  }
  if (params.search?.trim()) query.set('search', params.search.trim());
  if (params.productLines?.length) query.set('product_lines', params.productLines.join(','));
  if (params.productModelIds?.length) query.set('product_model_ids', params.productModelIds.join(','));
  const qs = query.toString();
  return authenticatedProApiFetch<ProductsListResponse>(qs ? `/products?${qs}` : '/products', accessToken);
}

export async function transferProductOwner(
  accessToken: string,
  productId: string,
  organizationId: string,
): Promise<Product> {
  return authenticatedProApiFetch<Product>(`/products/${productId}/owner`, accessToken, {
    method: 'PUT',
    body: JSON.stringify({ organizationId }),
  });
}

export async function getProductById(
  accessToken: string,
  productId: string,
): Promise<Product> {
  return authenticatedProApiFetch<Product>(`/products/${productId}`, accessToken);
}

export interface CreateProductPayload {
  organizationId: string;
  name: string;
  productModelId?: string;
  assetTag?: string;
  serial?: string;
  macAddress?: string;
  source?: ProductSource;
  isSimulated?: boolean;
  isMocked?: boolean;
  iotThingName?: string;
  pvCapacityW?: number;
  gridType?: ProductGridType;
  installLocation?: ProductInstallLocation;
  latitude?: number;
  longitude?: number;
  address?: string;
  addressSecondary?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  timezone?: string;
}

export async function createProduct(
  payload: CreateProductPayload,
  accessToken: string,
): Promise<Product> {
  return authenticatedProApiFetch<Product>('/products', accessToken, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export interface UpdateProductPayload {
  name?: string;
  productModelId?: string | null;
  assetTag?: string | null;
  serial?: string | null;
  macAddress?: string | null;
  iotThingName?: string | null;
  dataCatalogId?: string | null;
  source?: ProductSource;
  status?: ProductStatus;
  isSimulated?: boolean;
  pvCapacityW?: number | null;
  gridType?: ProductGridType | null;
  installLocation?: ProductInstallLocation | null;
  latitude?: number | null;
  longitude?: number | null;
  address?: string | null;
  addressSecondary?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  country?: string | null;
  timezone?: string | null;
}

export async function updateProduct(
  accessToken: string,
  productId: string,
  payload: UpdateProductPayload,
): Promise<Product> {
  return authenticatedProApiFetch<Product>(`/products/${productId}`, accessToken, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export async function fetchMockedProductCount(accessToken: string): Promise<number> {
  const res = await authenticatedProApiFetch<{ count: number }>('/products/mocked/count', accessToken);
  return res.count;
}

export async function clearMockedProducts(accessToken: string): Promise<number> {
  const res = await authenticatedProApiFetch<{ deleted: number }>('/products/mocked', accessToken, { method: 'DELETE' });
  return res.deleted;
}

export async function deleteProduct(
  accessToken: string,
  productId: string,
): Promise<void> {
  await authenticatedProApiFetch(`/products/${productId}`, accessToken, {
    method: 'DELETE',
  });
}

export async function hardDeleteProduct(
  accessToken: string,
  productId: string,
): Promise<void> {
  await authenticatedProApiFetch(`/products/${productId}/hard`, accessToken, {
    method: 'DELETE',
  });
}

// ─── Components ───────────────────────────────────────────────────────────────

export interface Component {
  id: string;
  productId: string;
  componentType: string;
  iotComponentId?: string | null;
  dataCatalogId?: string | null;
  dataCatalogName?: string | null;
  name?: string | null;
  componentIndex?: number | null;
  serial?: string | null;
  macAddress?: string | null;
  manufacturer?: string | null;
  model?: string | null;
  hwVersion?: string | null;
  fwVersion?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export type CreateComponentPayload = Omit<
  Component,
  'id' | 'productId' | 'dataCatalogName' | 'createdAt' | 'updatedAt'
>;
export type UpdateComponentPayload = Partial<CreateComponentPayload>;

export async function getComponentById(
  accessToken: string,
  componentId: string,
): Promise<Component> {
  return authenticatedProApiFetch<Component>(`/components/${componentId}`, accessToken);
}

export async function fetchComponents(
  accessToken: string,
  productId: string,
): Promise<Component[]> {
  return authenticatedProApiFetch<Component[]>(
    `/products/${productId}/components`,
    accessToken,
  );
}

export async function createComponent(
  accessToken: string,
  productId: string,
  payload: CreateComponentPayload,
): Promise<Component> {
  return authenticatedProApiFetch<Component>(
    `/products/${productId}/components`,
    accessToken,
    { method: 'POST', body: JSON.stringify(payload) },
  );
}

export async function updateComponent(
  accessToken: string,
  productId: string,
  componentId: string,
  payload: UpdateComponentPayload,
): Promise<Component> {
  return authenticatedProApiFetch<Component>(
    `/products/${productId}/components/${componentId}`,
    accessToken,
    { method: 'PATCH', body: JSON.stringify(payload) },
  );
}

export async function deleteComponent(
  accessToken: string,
  productId: string,
  componentId: string,
): Promise<void> {
  await authenticatedProApiFetch(
    `/products/${productId}/components/${componentId}`,
    accessToken,
    { method: 'DELETE' },
  );
}

// ─── Telemetry ────────────────────────────────────────────────────────────────

/** A single resolved code entry for alarm/fault/status array keys */
export interface CodeMatch {
  code: string;
  label: string;
  description?: string | null;
  severity?: string | null;
  alertLevel?: string | null;
}

/** A single enriched measure from the pipeline */
export interface EnrichedMeasure {
  /** Original raw value from the source */
  raw: number | string | null;
  /** Value in canonical_unit (scaled if raw_unit ≠ canonical_unit) */
  value: number | string | null;
  /** The unit of `value` */
  unit?: string;
  /** Human-readable label from the DataCatalog (falls back to raw key) */
  label: string;
  /** For single-enum: human label of the matched value.
   *  For code-array keys: descriptions joined by "; ". */
  description?: string | null;
  /** Highest severity across active codes */
  severity?: string | null;
  metadata?: Record<string, unknown> | null;
  /** For alarms/faults/status: one entry per active code in the raw JSON array */
  codes?: CodeMatch[];
}

/** Enriched telemetry snapshot for a single component */
export interface ComponentSnapshot {
  componentId: string;
  iotComponentId?: string;
  componentType: string;
  name?: string | null;
  componentIndex?: number | null;
  time?: string;
  measures: Record<string, EnrichedMeasure>;
  _mock?: boolean;
}

export interface TelemetrySnapshot {
  productId: string;
  time: string | null;
  /** Enriched, labelled, unit-converted measures. Each entry's `raw` field holds the original source value. */
  measures: Record<string, EnrichedMeasure>;
  /** Telemetry snapshots for each active component with an iotComponentId */
  components: ComponentSnapshot[];
  domain?: string;
  _mock?: boolean;
  _cached?: boolean;
  _throttled?: boolean;
  /** True when served from the IoT push cache because partner-api was unavailable. Data may be minutes or hours old. */
  _stale?: boolean;
  /** Approximate age of stale data in milliseconds. */
  _staleAgeMs?: number;
  /** True when no telemetry data is available at all (device offline, partner-api not configured, no IoT push yet). */
  _noData?: boolean;
  /** ISO timestamp of last telemetry payload received from the device. */
  lastTelemetryAt?: string | null;
  /** ISO timestamp of last shadow update received from the device. */
  lastShadowAt?: string | null;
  /** Max of lastTelemetryAt and lastShadowAt — use for health status derivation. */
  lastSignalAt?: string | null;
}

export interface TelemetryPage {
  results: TelemetrySnapshot[];
  pageInfo: { total: number; page: number; limit: number; hasNextPage: boolean };
  domain?: string;
  _mock?: boolean;
  _source?: 'timestream' | 'partner-api';
}

// ─── DataCatalog ──────────────────────────────────────────────────────────────

export interface DataCatalogKey {
  id: string;
  key: string;
  label: string;
  description?: string | null;
  valueType: string;
  rawUnit?: string | null;
  canonicalUnit?: string | null;
  precision?: number | null;
  sortOrder: number;
  isActive: boolean;
  enumGroupId?: string | null;
  // Shadow / settings fields
  kind?: 'realtime' | 'settings';
  section?: string | null;
  isEditable?: boolean;
  accessLevel?: string | null;
  defaultValue?: string | null;
  shadowName?: string | null;
  rawScale?: number | null;
}

export interface DataCatalogAlert {
  id: string;
  catalogId: string;
  code: string;
  name: string;
  type: 'alarm' | 'fault' | string;
  severity: number;
  description?: string | null;
  isActive: boolean;
  sortOrder: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface DataCatalog {
  id: string;
  slug?: string | null;
  name: string;
  productModelId?: string | null;
  firmwareVersion?: string | null;
  description?: string | null;
  isActive: boolean;
  keys?: DataCatalogKey[];
  alerts?: DataCatalogAlert[];
}

export async function fetchDataCatalogs(accessToken: string): Promise<DataCatalog[]> {
  const res = await authenticatedProApiFetch<{ data: DataCatalog[] }>('/data-catalogs', accessToken);
  return res.data;
}

export async function fetchDataCatalog(accessToken: string, id: string): Promise<DataCatalog> {
  return authenticatedProApiFetch<DataCatalog>(`/data-catalogs/${id}`, accessToken);
}

export async function fetchDataCatalogAlerts(
  accessToken: string,
  catalogId: string,
  opts?: { includeInactive?: boolean; type?: 'alarm' | 'fault' },
): Promise<DataCatalogAlert[]> {
  const qs = new URLSearchParams();
  if (opts?.includeInactive) qs.set('includeInactive', 'true');
  if (opts?.type) qs.set('type', opts.type);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const res = await authenticatedProApiFetch<{ data: DataCatalogAlert[]; total: number }>(
    `/data-catalogs/${catalogId}/alerts${suffix}`,
    accessToken,
  );
  return res.data ?? [];
}

/**
 * Resolve which data catalog a product uses — mirrors product-service catalog.service
 * and ProductDetailPage: override → product-line slug family → per-model exact match.
 */
export function resolveDataCatalogForProduct(
  product: Pick<Product, 'dataCatalogId' | 'productModelId' | 'productModel'>,
  catalogs: DataCatalog[],
): DataCatalog | null {
  const PRODUCT_LINE_TO_SLUG: Record<string, string> = {
    peak: 'sanctuary',
    sanctuary: 'sanctuary',
    sanctuary_1: 'sanctuary',
    sanctuary_2: 'sanctuary',
    sanctuary_3: 'sanctuary',
    vault: 'sanctuary',
    cabinet: 'cabinet',
    container: 'container',
  };
  const active = catalogs.filter((c) => c.isActive);
  if (product.dataCatalogId) {
    return active.find((c) => c.id === product.dataCatalogId) ?? null;
  }
  const line = product.productModel?.productLine ?? null;
  const slug = line ? PRODUCT_LINE_TO_SLUG[line] : undefined;
  return (
    (slug ? active.find((c) => c.slug === slug) ?? null : null) ??
    (product.productModelId
      ? active.find((c) => c.productModelId === product.productModelId) ?? null
      : null)
  );
}

export async function createDataCatalogAlert(
  accessToken: string,
  catalogId: string,
  body: {
    code: string;
    name: string;
    type: 'alarm' | 'fault';
    severity: number;
    description?: string | null;
    isActive?: boolean;
    sortOrder?: number;
  },
): Promise<DataCatalogAlert> {
  return authenticatedProApiFetch<DataCatalogAlert>(
    `/data-catalogs/${catalogId}/alerts`,
    accessToken,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

export async function updateDataCatalogAlert(
  accessToken: string,
  catalogId: string,
  alertId: string,
  body: Partial<{
    code: string;
    name: string;
    type: 'alarm' | 'fault';
    severity: number;
    description: string | null;
    isActive: boolean;
    sortOrder: number;
  }>,
): Promise<DataCatalogAlert> {
  return authenticatedProApiFetch<DataCatalogAlert>(
    `/data-catalogs/${catalogId}/alerts/${alertId}`,
    accessToken,
    { method: 'PATCH', body: JSON.stringify(body) },
  );
}

export async function deleteDataCatalogAlert(
  accessToken: string,
  catalogId: string,
  alertId: string,
): Promise<void> {
  await authenticatedProApiFetch<void>(
    `/data-catalogs/${catalogId}/alerts/${alertId}`,
    accessToken,
    { method: 'DELETE' },
  );
}

export async function bulkDeleteDataCatalogAlerts(
  accessToken: string,
  catalogId: string,
  ids: string[],
): Promise<{ deleted: number }> {
  return authenticatedProApiFetch<{ deleted: number }>(
    `/data-catalogs/${catalogId}/alerts/bulk-delete`,
    accessToken,
    { method: 'POST', body: JSON.stringify({ ids }) },
  );
}

export async function deleteDataCatalogKey(
  accessToken: string,
  catalogId: string,
  keyId: string,
): Promise<void> {
  await authenticatedProApiFetch<void>(
    `/data-catalogs/${catalogId}/keys/${keyId}`,
    accessToken,
    { method: 'DELETE' },
  );
}

export async function bulkDeleteDataCatalogKeys(
  accessToken: string,
  catalogId: string,
  ids: string[],
): Promise<{ deleted: number }> {
  return authenticatedProApiFetch<{ deleted: number }>(
    `/data-catalogs/${catalogId}/keys/bulk-delete`,
    accessToken,
    { method: 'POST', body: JSON.stringify({ ids }) },
  );
}

export type ProductAlertResolvedSource = 'device_event' | 'stale_expired' | 'manual_dismiss';

export type ProductAlertEpisode = {
  /**
   * The incident's real id — needed to target a specific incident (e.g. to
   * dismiss it). Deliberately NOT called `id`: callers spread this row into
   * their own AlertRow shape and then set their own `id` (a synthetic
   * per-render key used for Timeline row/selection identity) — naming this
   * field `id` too would let that spread-then-override silently discard it.
   */
  incidentId: string;
  code: string;
  name: string;
  type: string | null;
  severity: number | null;
  description: string | null;
  openedAt: string;
  closedAt: string | null;
  active: boolean;
  resolvedSource: ProductAlertResolvedSource | null;
  resolvedByUserId: string | null;
  resolvedByUserName: string | null;
  resolutionNote: string | null;
};

export async function fetchProductAlertHistory(
  accessToken: string,
  productId: string,
  params?: { start?: string; end?: string; codes?: string[] },
): Promise<{ data: ProductAlertEpisode[]; total: number }> {
  const qs = new URLSearchParams();
  if (params?.start) qs.set('start', params.start);
  if (params?.end) qs.set('end', params.end);
  if (params?.codes?.length) qs.set('codes', params.codes.join(','));
  const q = qs.toString();
  return authenticatedProApiFetch(
    `/products/${productId}/alerts/history${q ? `?${q}` : ''}`,
    accessToken,
  );
}

export async function fetchProductActiveAlerts(
  accessToken: string,
  productId: string,
): Promise<{ data: ProductAlertEpisode[]; total: number }> {
  return authenticatedProApiFetch(`/products/${productId}/alerts/active`, accessToken);
}

/** Manually closes a still-open incident, recording a reason and the current user. */
export async function dismissProductAlert(
  accessToken: string,
  productId: string,
  incidentId: string,
  reason: string,
): Promise<ProductAlertEpisode> {
  return authenticatedProApiFetch(`/products/${productId}/alerts/${incidentId}/dismiss`, accessToken, {
    method: 'PATCH',
    body: JSON.stringify({ reason }),
  });
}

/** A ProductAlertEpisode tagged with the product it belongs to — only returned by the bulk endpoint. */
export type ProductAlertEpisodeWithProduct = ProductAlertEpisode & { productId: string };

/** Bulk incident history across multiple products at once — powers the cross-product Alerts Timeline. */
export async function fetchProductsAlertHistoryBulk(
  accessToken: string,
  params: { ids: string[]; start?: string; end?: string },
): Promise<{ data: ProductAlertEpisodeWithProduct[]; total: number; start: string; end: string }> {
  const qs = new URLSearchParams();
  qs.set('ids', params.ids.join(','));
  if (params.start) qs.set('start', params.start);
  if (params.end) qs.set('end', params.end);
  return authenticatedProApiFetch(`/products/alerts/history?${qs.toString()}`, accessToken);
}

// ─── Telemetry ────────────────────────────────────────────────────────────────

export async function fetchTelemetryLatest(
  accessToken: string,
  productId: string,
  keys?: string[],
): Promise<TelemetrySnapshot> {
  const qs = keys && keys.length > 0 ? `?keys=${keys.join(',')}` : '';
  return authenticatedProApiFetch<TelemetrySnapshot>(
    `/products/${productId}/telemetry/latest${qs}`,
    accessToken,
  );
}

export async function fetchTelemetryRange(
  accessToken: string,
  productId: string,
  params: { start?: string; end?: string; limit?: number; page?: number; keys?: string[] },
): Promise<TelemetryPage> {
  const qs = new URLSearchParams();
  if (params.start) qs.set('start', params.start);
  if (params.end) qs.set('end', params.end);
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.page !== undefined) qs.set('page', String(params.page));
  if (params.keys?.length) qs.set('keys', params.keys.join(','));
  const query = qs.toString();
  return authenticatedProApiFetch<TelemetryPage>(
    `/products/${productId}/telemetry${query ? `?${query}` : ''}`,
    accessToken,
  );
}

// ─── EnumGroups ───────────────────────────────────────────────────────────────

export interface EnumGroupValue {
  id: string;
  groupId: string;
  key: string;
  label: string;
  description?: string | null;
  meta?: Record<string, unknown> | null;
  sortOrder: number;
  isActive: boolean;
}

export interface EnumGroup {
  id: string;
  name: string;
  description?: string | null;
  isActive: boolean;
  valueCount?: number;
  values?: EnumGroupValue[];
  createdAt: string;
  updatedAt: string;
}

export async function fetchEnumGroups(accessToken: string): Promise<EnumGroup[]> {
  const res = await authenticatedProApiFetch<{ data: EnumGroup[]; total: number }>('/enum-groups', accessToken);
  return res.data;
}

export async function fetchEnumGroup(accessToken: string, id: string): Promise<EnumGroup> {
  return authenticatedProApiFetch<EnumGroup>('/enum-groups/' + id, accessToken);
}

export async function createEnumGroup(
  accessToken: string,
  payload: { name: string; description?: string | null },
): Promise<EnumGroup> {
  return authenticatedProApiFetch<EnumGroup>('/enum-groups', accessToken, {
    method: 'POST', body: JSON.stringify(payload),
  });
}

export async function updateEnumGroup(
  accessToken: string,
  id: string,
  payload: { name?: string; description?: string | null; isActive?: boolean },
): Promise<EnumGroup> {
  return authenticatedProApiFetch<EnumGroup>('/enum-groups/' + id, accessToken, {
    method: 'PATCH', body: JSON.stringify(payload),
  });
}

export async function createEnumGroupValue(
  accessToken: string,
  groupId: string,
  payload: { key: string; label: string; description?: string | null; meta?: Record<string, unknown> | null; sortOrder?: number },
): Promise<EnumGroupValue> {
  return authenticatedProApiFetch<EnumGroupValue>('/enum-groups/' + groupId + '/values', accessToken, {
    method: 'POST', body: JSON.stringify(payload),
  });
}

export async function updateEnumGroupValue(
  accessToken: string,
  groupId: string,
  valueId: string,
  payload: Partial<{ label: string; description: string | null; meta: Record<string, unknown> | null; sortOrder: number; isActive: boolean }>,
): Promise<EnumGroupValue> {
  return authenticatedProApiFetch<EnumGroupValue>('/enum-groups/' + groupId + '/values/' + valueId, accessToken, {
    method: 'PATCH', body: JSON.stringify(payload),
  });
}

export async function deleteEnumGroupValue(
  accessToken: string,
  groupId: string,
  valueId: string,
): Promise<void> {
  await authenticatedProApiFetch('/enum-groups/' + groupId + '/values/' + valueId, accessToken, { method: 'DELETE' });
}

// ─── Import from Smart ────────────────────────────────────────────────────────

export type ProductGridType = 'on_grid' | 'off_grid';
export type ProductInstallLocation = 'ground_mount' | 'roof_top';

// ─── IoT Shadows ─────────────────────────────────────────────────────────────

export interface ShadowState {
  state: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  version?: number;
  timestamp?: number;
  _mock?: boolean;
  _simulated?: boolean;
}

export async function fetchProductShadows(
  accessToken: string,
  productId: string,
): Promise<{ shadows: string[]; iotThingName: string }> {
  return authenticatedProApiFetch(
    `/products/${productId}/shadows`,
    accessToken,
  );
}

/** Returns the distinct shadow names that have catalog settings keys for this product. */
export async function fetchProductSettingShadowNames(
  accessToken: string,
  productId: string,
): Promise<string[]> {
  const res = await authenticatedProApiFetch<{ shadowNames: string[] }>(
    `/products/${productId}/shadows/settings/shadow-names`,
    accessToken,
  );
  return res.shadowNames ?? [];
}

export async function fetchProductShadow(
  accessToken: string,
  productId: string,
  shadowName: string,
): Promise<{ shadow: ShadowState; shadowName: string; iotThingName: string | null }> {
  const res = await authenticatedProApiFetch<any>(
    `/products/${productId}/shadows/${shadowName}`,
    accessToken,
  );
  // Backend returns either { shadow, shadowName, iotThingName } (wrapped)
  // or the ShadowState directly (unwrapped). Handle both.
  if (res && res.shadow) return res;
  return { shadow: res as ShadowState, shadowName, iotThingName: null };
}

export async function updateProductShadow(
  accessToken: string,
  productId: string,
  shadowName: string,
  desiredState: Record<string, unknown>,
): Promise<{ shadow: ShadowState; shadowName: string; iotThingName: string }> {
  return authenticatedProApiFetch(
    `/products/${productId}/shadows/${shadowName}`,
    accessToken,
    { method: 'PATCH', body: JSON.stringify(desiredState) },
  );
}

// ─── Simulation control ───────────────────────────────────────────────────────

export interface SimulationStartResult {
  isSimulating: boolean;
  simulationExpAt: string;
  simulationStartedAt: string;
}

export async function startProductSimulation(
  accessToken: string,
  productId: string,
  durationMs: number,
): Promise<SimulationStartResult> {
  return authenticatedProApiFetch<SimulationStartResult>(
    `/products/${productId}/simulation/start`,
    accessToken,
    { method: 'POST', body: JSON.stringify({ durationMs }) },
  );
}

export async function pauseProductSimulation(
  accessToken: string,
  productId: string,
): Promise<{ isSimulating: boolean; simulationExpAt: null }> {
  return authenticatedProApiFetch(
    `/products/${productId}/simulation/pause`,
    accessToken,
    { method: 'POST' },
  );
}

export interface SimulationStateResult {
  isSimulating: boolean;
  simulationExpAt: string | null;
  simulationStartedAt: string | null;
  activeAlerts?: Array<{ code: string; type: string; name?: string; severity?: number }>;
  /** @deprecated Prefer activeAlerts — last opened alert for compatibility. */
  activeAlert?: { code: string; type: string } | null;
}

export async function getProductSimulationState(
  accessToken: string,
  productId: string,
): Promise<SimulationStateResult> {
  return authenticatedProApiFetch<SimulationStateResult>(
    `/products/${productId}/simulation/state`,
    accessToken,
  );
}

export type SimulatedAlertItem = {
  code: string;
  name: string;
  type: string;
  severity: number;
  description?: string | null;
};

export type SimulatedAlertResult = {
  activeAlerts: SimulatedAlertItem[];
  activeAlert: SimulatedAlertItem | null;
};

export async function openProductSimulationAlert(
  accessToken: string,
  productId: string,
  code: string,
): Promise<SimulatedAlertResult> {
  return authenticatedProApiFetch<SimulatedAlertResult>(
    `/products/${productId}/simulation/alerts`,
    accessToken,
    { method: 'POST', body: JSON.stringify({ action: 'open', code }) },
  );
}

export async function clearProductSimulationAlert(
  accessToken: string,
  productId: string,
  code?: string,
): Promise<SimulatedAlertResult> {
  return authenticatedProApiFetch<SimulatedAlertResult>(
    `/products/${productId}/simulation/alerts`,
    accessToken,
    {
      method: 'POST',
      body: JSON.stringify({ action: 'clear', ...(code ? { code } : {}) }),
    },
  );
}

// ─── Shadow Settings ──────────────────────────────────────────────────────────

export type SettingStatus = 'synced' | 'pending' | 'unconfirmed';

export interface EnrichedSetting {
  key: string;
  reportedValue: string | null;
  desiredValue?: string;
  status: SettingStatus;
  lastSyncAt?: number;
  label: string;
  description?: string | null;
  valueType: string;
  unit?: string | null;
  precision?: number | null;
  section?: string | null;
  isEditable: boolean;
  accessLevel: string;
  enumOptions?: Array<{ value: string; label: string }>;
  /** Scale factor applied to convert raw integer values to display units (e.g. 0.01 for W→kW). */
  rawScale?: number | null;
}

export interface ShadowPipelineResult {
  productId: string;
  shadowName: string;
  settings: EnrichedSetting[];
  shadowVersion?: number;
  shadowTimestamp?: number;
  /** Unix ms of the most recent reported key timestamp across all keys in this shadow */
  lastDeviceSync?: number;
  _simulated?: boolean;
  _mock?: boolean;
}

export async function fetchProductShadowSettings(
  accessToken: string,
  productId: string,
  shadowName: string,
): Promise<ShadowPipelineResult> {
  return authenticatedProApiFetch<ShadowPipelineResult>(
    `/products/${productId}/shadows/${shadowName}/settings`,
    accessToken,
  );
}

export async function updateProductShadowSettings(
  accessToken: string,
  productId: string,
  shadowName: string,
  datum: Record<string, string>,
): Promise<{ ok: true; shadowName: string; updatedKeys: string[] }> {
  return authenticatedProApiFetch(
    `/products/${productId}/shadows/${shadowName}/settings`,
    accessToken,
    { method: 'PATCH', body: JSON.stringify({ datum }) },
  );
}
